/**
 * Precedent: APR contextual help — `apps/portal/web/src/pages/apr/compat/HelpPanel.tsx`
 * Cloned, not imported (DECISIONS.md D-003). Nothing under pages/apr/ or
 * pages/master-data/ is modified by this module.
 *
 * Same structure, same order, same copy for the fixed labels ("How this works", "What you do
 * here", "Where it fits", "First time? Do this"), same Escape-to-close, same tab strip when
 * a screen passes `helps[]`. The class names carry this module's `erp-hlp-` prefix for the
 * reason `HelpPanel.css` records: APR's are bare and would otherwise style this panel too.
 *
 * The one behaviour worth naming because it is load-bearing and easy to lose: the `defs`
 * term wears a WASH plus a FULL-STRENGTH RULE in the term's own colour (`${color}24` with a
 * `1px solid ${color}`), and APR's comment says why the rule is not a third of the colour —
 * at `55` it read as a soft outline on a pill rather than the edge of a chip.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { type Help, type HelpDefItem, type HelpLink, type HelpSection } from './helpTypes';
import './HelpPanel.css';

type HelpPanelProps = {
  help?: Help;
  helps?: Help[];
  activeIndex?: number;
  onTabChange?: (i: number) => void;
};

const DEFAULT_ICON = 'ℹ️';
const DEFAULT_TITLE = 'How this works';

export function HelpPanel({ help, helps, activeIndex, onTabChange }: HelpPanelProps) {
  const panes: Help[] = helps ?? [help ?? {}];
  const multi = panes.length > 1;

  const [open, setOpen] = useState(false);
  const [internalActive, setInternalActive] = useState(0);

  const controlled = activeIndex !== undefined;
  const rawActive = controlled ? (activeIndex as number) : internalActive;
  const active = rawActive < 0 || rawActive >= panes.length ? 0 : rawActive;

  const close = useCallback(() => setOpen(false), []);

  const selectTab = useCallback(
    (i: number) => {
      const idx = i < 0 || i >= panes.length ? 0 : i;
      if (!controlled) setInternalActive(idx);
      if (onTabChange) onTabChange(idx);
    },
    [controlled, onTabChange, panes.length],
  );

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [close]);

  const pane = panes[active] ?? {};

  return (
    <>
      <button
        type="button"
        className="erp-hlp-launch"
        onClick={() => setOpen((o) => !o)}
        title="How this works"
      >
        <span className="erp-hlp-q">?</span> How this works
      </button>

      <div className={`erp-hlp-overlay${open ? ' on' : ''}`} onClick={close} />
      <aside
        className={`erp-hlp-drawer${open ? ' on' : ''}`}
        role="dialog"
        aria-label="How this works"
      >
        <div className="erp-hlp-head">
          <div className="erp-hlp-title">
            <span className="erp-hlp-ic">{pane.icon || DEFAULT_ICON}</span>
            <span>{pane.title || DEFAULT_TITLE}</span>
          </div>
          <button className="erp-hlp-x" onClick={close} aria-label="Close">
            &times;
          </button>
        </div>

        {multi && (
          <div className="erp-hlp-tabs" role="tablist">
            {panes.map((h, i) => (
              <button
                key={i}
                type="button"
                className={`erp-hlp-tab${i === active ? ' on' : ''}`}
                onClick={() => selectTab(i)}
              >
                {h.tabLabel || h.title}
              </button>
            ))}
          </div>
        )}

        <div className="erp-hlp-body">
          {pane.purpose && (
            <p className="erp-hlp-purpose" dangerouslySetInnerHTML={{ __html: pane.purpose }} />
          )}

          {pane.doHere && pane.doHere.length > 0 && (
            <>
              <div className="erp-hlp-lbl">What you do here</div>
              <ul className="erp-hlp-list">
                {pane.doHere.map((d, i) => (
                  <li key={i} dangerouslySetInnerHTML={{ __html: d }} />
                ))}
              </ul>
            </>
          )}

          {pane.fits && (
            <>
              <div className="erp-hlp-lbl">Where it fits</div>
              <div className="erp-hlp-fits" dangerouslySetInnerHTML={{ __html: pane.fits }} />
            </>
          )}

          {pane.firstTime && (
            <div className="erp-hlp-first">
              <span className="erp-hlp-first-ic">&#9654;</span>
              <div className="erp-hlp-first-c">
                <div className="erp-hlp-first-lbl">First time? Do this</div>
                <div
                  className="erp-hlp-first-txt"
                  dangerouslySetInnerHTML={{ __html: pane.firstTime }}
                />
              </div>
            </div>
          )}

          {pane.sections &&
            pane.sections.length > 0 &&
            pane.sections.map((sec: HelpSection, si: number) => (
              <React.Fragment key={si}>
                {sec.label && <div className="erp-hlp-lbl">{sec.label}</div>}
                {sec.type === 'defs' ? (
                  <>
                    <div className="erp-hlp-defs">
                      {(sec.items || []).map((it: HelpDefItem, ii: number) => (
                        <div className="erp-hlp-def" key={ii}>
                          <span
                            className="erp-hlp-def-term"
                            style={
                              it.color
                                ? {
                                    // Wash and a full-strength rule, exactly as the chips
                                    // on the screens do it. The rule used to be `55` — a
                                    // third of the colour — which read as a soft outline on
                                    // a pill, not as the edge of a chip.
                                    background: `${it.color}24`,
                                    color: it.color,
                                    border: `1px solid ${it.color}`,
                                  }
                                : undefined
                            }
                          >
                            {it.term}
                          </span>
                          <span
                            className="erp-hlp-def-txt"
                            dangerouslySetInnerHTML={{ __html: it.def }}
                          />
                        </div>
                      ))}
                    </div>
                    {sec.note && (
                      <p className="erp-hlp-note" dangerouslySetInnerHTML={{ __html: sec.note }} />
                    )}
                  </>
                ) : (
                  <div
                    className="erp-hlp-sec-html"
                    dangerouslySetInnerHTML={{ __html: sec.html }}
                  />
                )}
              </React.Fragment>
            ))}

          {pane.links && pane.links.length > 0 && (
            <div className="erp-hlp-links">
              {pane.links.map((l: HelpLink, i: number) => (
                <a key={i} href={l.href} className="erp-hlp-link">
                  {l.label}
                </a>
              ))}
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
